import { NavLink, Routes, Route } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Avatar, Button, FormField, Input, Switch, ControlRow, cn, useToast } from '@vd/ui';
import type { UserTheme } from '@vd/types';
import { useAuth } from '../../features/auth/AuthProvider';
import { useTheme } from '../../app/theme';
import { api } from '../../lib/api';
import { ExtensionSetupTab } from './tabs/ExtensionSetupTab';
import { McpSetupTab } from './tabs/McpSetupTab';
import styles from './Settings.module.css';
import { useTabTitle } from '../../lib/tabTitle';
import { When } from '../../lib/When';
import { browserTimeZone, setDisplayTimeZone } from '../../lib/time';

// Absolute paths — required because the parent route (`/me/settings/*`)
// is a splat and we've opted into `v7_relativeSplatPath` (App.tsx). Under
// that flag, a relative `to="profile"` from `/me/settings/notifications`
// would resolve to `/me/settings/notifications/profile` (which matches
// nothing) instead of replacing the splat segment.
const TAB_PREFIX = '/me/settings';
const tabs = [
  { slug: 'profile', label: 'Profile' },
  { slug: 'notifications', label: 'Notifications' },
  { slug: 'extension', label: 'Extension' },
  { slug: 'mcp-setup', label: 'MCP Connector' },
  { slug: 'api-tokens', label: 'API tokens' },
  { slug: 'privacy', label: 'Privacy' },
  { slug: 'sessions', label: 'Sessions' },
];

export function UserSettingsPage() {
  // This tab's title (TOADO-905).
  useTabTitle({ parts: ['Settings'] });
  return (
    <div className={styles.page}>
      <aside className={styles.nav}>
        <div className={styles.navTitle}>Your settings</div>
        {tabs.map((t) => (
          <NavLink
            key={t.slug}
            to={`${TAB_PREFIX}/${t.slug}`}
            className={({ isActive }: { isActive: boolean }) => cn(styles.navItem, isActive && styles.navActive)}
          >
            {t.label}
          </NavLink>
        ))}
      </aside>
      <main className={styles.content}>
        <Routes>
          <Route path="/" element={<ProfileTab />} />
          <Route path="profile" element={<ProfileTab />} />
          <Route path="notifications" element={<NotificationsTab />} />
          <Route path="extension" element={<ExtensionSetupTab />} />
          <Route path="mcp-setup" element={<McpSetupTab />} />
          <Route path="api-tokens" element={<ApiTokensTab />} />
          <Route path="privacy" element={<PrivacyTab />} />
          <Route path="sessions" element={<SessionsTab />} />
        </Routes>
      </main>
    </div>
  );
}

function ProfileTab() {
  const { user, refreshUser } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (user) {
      setName(user.name);
      setEmail(user.email);
    }
  }, [user?.name, user?.email]);

  if (!user) return null;

  const dirty = name !== user.name || email !== user.email;
  const save = async () => {
    setSaving(true);
    try {
      const payload: { name?: string; email?: string } = {};
      if (name !== user.name) payload.name = name;
      if (email !== user.email) payload.email = email;
      await api.updateMe(payload);
      // AuthProvider holds `user` in plain state, not React Query, so
      // `invalidateQueries` alone doesn't refresh the shell header or
      // this tab's form on next mount. Pull /me through the provider.
      await refreshUser();
      await qc.invalidateQueries({ queryKey: ['me'] });
      toast.push({
        variant: 'success',
        title: 'Profile saved',
        description: email !== user.email ? 'Check your inbox to verify the new email.' : undefined,
      });
    } catch (e) {
      toast.push({ variant: 'danger', title: 'Save failed', description: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  // Avatar upload — picks the first file the user selected, sends it
  // multipart, refreshes /me so the new URL flows through the shell.
  const onAvatarPicked = async (file: File) => {
    if (file.size > 2 * 1024 * 1024) {
      toast.push({ variant: 'danger', title: 'Avatar too large', description: 'Max 2 MB.' });
      return;
    }
    setUploadingAvatar(true);
    try {
      await api.uploadAvatar(file);
      await refreshUser();
      toast.push({ variant: 'success', title: 'Photo updated' });
    } catch (e) {
      toast.push({ variant: 'danger', title: 'Upload failed', description: (e as Error).message });
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Reverts to the gradient + initials default. Server clears the
  // avatar_url column; the file itself is left in storage (harmless,
  // and a future re-upload overwrites the same key).
  const onClearAvatar = async () => {
    setUploadingAvatar(true);
    try {
      await api.deleteAvatar();
      await refreshUser();
      toast.push({ variant: 'success', title: 'Photo cleared' });
    } catch (e) {
      toast.push({ variant: 'danger', title: 'Clear failed', description: (e as Error).message });
    } finally {
      setUploadingAvatar(false);
    }
  };

  return (
    <>
      <h1 className={styles.h1}>Profile</h1>
      <section className={styles.section}>
        <div className={styles.row} style={{ marginBottom: 16 }}>
          <Avatar name={user.name} src={user.avatarUrl} size="lg" />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void onAvatarPicked(file);
              // Reset so picking the same file again still triggers change.
              e.target.value = '';
            }}
          />
          <Button
            variant="secondary"
            size="sm"
            loading={uploadingAvatar}
            onClick={() => fileInputRef.current?.click()}
          >
            {user.avatarUrl ? 'Change photo' : 'Upload photo'}
          </Button>
          {user.avatarUrl && (
            <Button
              variant="ghost"
              size="sm"
              disabled={uploadingAvatar}
              onClick={onClearAvatar}
            >
              Clear
            </Button>
          )}
        </div>
        <FormField label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} /></FormField>
        <div style={{ height: 12 }} />
        <FormField label="Email" help="Changing email requires re-verification.">
          <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" />
        </FormField>
        <div style={{ height: 16 }} />
        <Button variant="primary" disabled={!dirty || !name.trim() || !email.trim() || saving} loading={saving} onClick={save}>Save</Button>
      </section>
      <AppearanceSection />
      <TimeZoneSection />
    </>
  );
}

/** Every zone this browser knows, or at least the two in play when it cannot list them. */
function zoneChoices(current: string): string[] {
  let zones: string[] = [];
  try {
    zones = (Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf?.('timeZone') ?? [];
  } catch { /* an older browser */ }
  return [...new Set([...zones, current, browserTimeZone(), 'UTC'])].sort();
}

/**
 * The time zone every time is shown in (TOADO-904). It follows this browser
 * until someone picks one; a picked zone stays put when they travel, until
 * they switch back to following the device.
 */
function TimeZoneSection() {
  const { user, refreshUser } = useAuth();
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const current = user?.timeZone ?? browserTimeZone();
  const manual = user?.timeZoneSource === 'manual';
  const choices = useMemo(() => zoneChoices(current), [current]);
  const save = async (timeZone: string, timeZoneSource: 'auto' | 'manual') => {
    setSaving(true);
    try {
      await api.patchPreferences({ timeZone, timeZoneSource });
      setDisplayTimeZone(timeZone);
      await refreshUser();
      toast.push({ variant: 'success', title: 'Time zone saved' });
    } catch (e) {
      toast.push({ variant: 'danger', title: 'Save failed', description: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };
  return (
    <section className={styles.section} style={{ marginTop: 16 }} data-testid="time-zone-section">
      <h2 className={styles.h}>Time zone</h2>
      <p className={styles.sub}>
        Every time in Toado is shown in this zone, and emails use it too.
        {' '}{manual ? 'Set by you.' : 'Detected from this browser.'}
      </p>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <select
          aria-label="Time zone"
          value={current}
          disabled={saving}
          onChange={(event) => void save(event.target.value, 'manual')}
          style={{ font: 'inherit', padding: '6px 8px', borderRadius: 'var(--r-md)', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)' }}
        >
          {choices.map((zone) => <option key={zone} value={zone}>{zone.replace(/_/g, ' ')}</option>)}
        </select>
        {manual && (
          <Button variant="ghost" size="sm" disabled={saving} onClick={() => void save(browserTimeZone(), 'auto')}>
            Use this device's time zone ({browserTimeZone().replace(/_/g, ' ')})
          </Button>
        )}
      </div>
    </section>
  );
}

const THEME_CHOICES: Array<{ value: UserTheme; label: string; help: string }> = [
  { value: 'system', label: 'System', help: 'Follow this device' },
  { value: 'light', label: 'Light', help: 'Always light' },
  { value: 'dark', label: 'Dark', help: 'Always dark' },
];

/**
 * The theme, saved to the account rather than to this browser.
 *
 * It lives on the account because the browser extension's in-page panel and
 * its pin composer use it too, and they cannot read this origin's
 * localStorage. That is also why this says "every Toado surface" rather than
 * "this app": choosing dark here darkens the panel on the next page you pin.
 */
function AppearanceSection() {
  const { preference, setPreference } = useTheme();
  return (
    <section className={styles.section} style={{ marginTop: 16 }}>
      <h2 className={styles.h}>Appearance</h2>
      <p className={styles.sub}>
        Applies to every Toado surface on your account, including the page-pin panel in the
        browser extension.
      </p>
      {/* Toggle buttons, one pressed: radios would promise arrow keys these do not take. */}
      <div role="group" aria-label="Theme" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {THEME_CHOICES.map((choice) => {
          const active = preference === choice.value;
          return (
            <button
              key={choice.value}
              type="button"
              aria-pressed={active}
              onClick={() => setPreference(choice.value)}
              style={{
                display: 'grid',
                gap: 2,
                minWidth: 116,
                padding: '9px 12px',
                textAlign: 'left',
                cursor: 'pointer',
                borderRadius: 'var(--r-md)',
                border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
                background: active ? 'color-mix(in srgb, var(--accent) 12%, var(--surface))' : 'var(--surface)',
                color: 'var(--text)',
                font: 'inherit',
              }}
            >
              <strong style={{ fontSize: 13 }}>{choice.label}</strong>
              <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{choice.help}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

const NOTIFICATION_KEYS = [
  { key: 'notify_mcp_comment',    label: 'New AI comment on my ticket',         default: true  },
  { key: 'notify_ticket_moved',   label: 'Ticket moved to my column',           default: true  },
  { key: 'notify_invite',         label: 'Invite received',                     default: true  },
  { key: 'notify_mention_email',  label: 'Daily digest when someone @-mentions me', default: true },
  { key: 'notify_weekly',         label: 'Weekly digest',                       default: false },
] as const;

function useTogglePref(initial: boolean, prefKey: string, afterSave?: (next: boolean) => void) {
  const qc = useQueryClient();
  const toast = useToast();
  const [value, setValue] = useState(initial);
  useEffect(() => setValue(initial), [initial]);
  const onChange = async (next: boolean) => {
    setValue(next);
    try {
      await api.patchPreferences({ [prefKey]: next });
      await qc.invalidateQueries({ queryKey: ['preferences'] });
      afterSave?.(next);
    } catch (e) {
      setValue(!next);
      toast.push({ variant: 'danger', title: 'Save failed', description: (e as Error).message });
    }
  };
  return [value, onChange] as const;
}

function NotificationsTab() {
  const { data: prefs = {} } = useQuery({ queryKey: ['preferences'], queryFn: () => api.getPreferences() });
  return (
    <>
      <h1 className={styles.h1}>Notifications</h1>
      <p className={styles.sub}>Toggles save to your account and sync across devices.</p>
      <section className={styles.section}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {NOTIFICATION_KEYS.map((n) => (
            <NotificationToggle
              key={n.key}
              prefKey={n.key}
              label={n.label}
              current={(prefs as Record<string, unknown>)[n.key] as boolean | undefined ?? n.default}
            />
          ))}
        </div>
      </section>
    </>
  );
}

function NotificationToggle({ prefKey, label, current }: { prefKey: string; label: string; current: boolean }) {
  const [value, onChange] = useTogglePref(current, prefKey);
  return <ControlRow><Switch checked={value} onChange={(e) => onChange(e.target.checked)} /> {label}</ControlRow>;
}

function ApiTokensTab() {
  const toast = useToast();
  return (
    <>
      <h1 className={styles.h1}>API tokens</h1>
      <p className={styles.sub}>
        Personal tokens for custom integrations you own. For Claude Code, use
        the MCP tokens tab in company settings.
      </p>
      <section className={styles.section}>
        <Button
          variant="primary"
          onClick={() => toast.push({ variant: 'info', title: 'Use MCP tokens', description: 'Personal tokens share the same create flow as MCP tokens in company settings.' })}
        >
          Create token
        </Button>
      </section>
    </>
  );
}

function PrivacyTab() {
  const { data: prefs = {} } = useQuery({ queryKey: ['preferences'], queryFn: () => api.getPreferences() });
  const current = (prefs as Record<string, unknown>).telemetry_opt_in;
  const initial = current === undefined ? true : !!current;
  const [value, onChange] = useTogglePref(initial, 'telemetry_opt_in');
  return (
    <>
      <h1 className={styles.h1}>Privacy</h1>
      <section className={styles.section}>
        <ControlRow>
          <Switch checked={value} onChange={(e) => onChange(e.target.checked)} />
          Share product telemetry
        </ControlRow>
        <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 8 }}>
          Anonymous metrics help us improve the product. Disabling will not affect functionality.
        </p>
      </section>
    </>
  );
}

function SessionsTab() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data: sessions = [] } = useQuery({ queryKey: ['me-sessions'], queryFn: () => api.listSessions() });

  const revokeOne = async (id: string) => {
    try {
      await api.revokeSession(id);
      await qc.invalidateQueries({ queryKey: ['me-sessions'] });
      toast.push({ variant: 'success', title: 'Session signed out' });
    } catch (e) {
      toast.push({ variant: 'danger', title: 'Sign-out failed', description: (e as Error).message });
    }
  };
  const revokeAll = async () => {
    try {
      await api.signOutOtherSessions();
      await qc.invalidateQueries({ queryKey: ['me-sessions'] });
      toast.push({ variant: 'success', title: 'All other sessions signed out' });
    } catch (e) {
      toast.push({ variant: 'danger', title: 'Sign-out-all failed', description: (e as Error).message });
    }
  };

  const otherCount = sessions.filter((s) => !s.isCurrent).length;

  return (
    <>
      <h1 className={styles.h1}>Sessions</h1>
      <p className={styles.sub}>Active browser and extension sessions signed in as you.</p>
      <section className={styles.section}>
        <table className={styles.table}>
          <thead><tr><th>Device</th><th>IP</th><th>Last active</th><th></th></tr></thead>
          <tbody>
            {sessions.map((s) => (
              <tr key={s.id}>
                <td>{s.userAgent ? shortUA(s.userAgent) : 'Unknown'}</td>
                <td style={{ color: 'var(--text-secondary)' }}>{maskIp(s.ipAddress)}</td>
                <td style={{ color: 'var(--text-secondary)' }}>
                  {s.isCurrent ? 'Now (this session)' : <When at={s.lastSeenAt} />}
                </td>
                <td>
                  {!s.isCurrent && (
                    <Button variant="ghost" size="sm" onClick={() => revokeOne(s.id)}>Sign out</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ height: 12 }} />
        <Button variant="destructive" disabled={otherCount === 0} onClick={revokeAll}>
          {otherCount === 0 ? 'No other sessions' : `Sign out all other sessions (${otherCount})`}
        </Button>
      </section>
    </>
  );
}

function shortUA(ua: string): string {
  if (ua.includes('Chrome/')) return 'Chrome';
  if (ua.includes('Firefox/')) return 'Firefox';
  if (ua.includes('Safari/') && !ua.includes('Chrome')) return 'Safari';
  if (ua.includes('Edg/')) return 'Edge';
  if (ua.toLowerCase().includes('extension')) return 'Chrome extension';
  return ua.slice(0, 40);
}

// Show enough of the address for the user to recognize "yes, this is
// me" without exposing the precise host (which would let a screenshot
// leak the user's exact location to anyone who sees the image).
//
//   IPv4: keep the first 2 octets, mask the rest. Reveals the AS owner
//         and rough region without identifying the host machine.
//   IPv6: keep the first 3 hextets, mask the rest. Same idea — enough
//         for "is this my home network?" recognition, not enough for
//         household-level identification.
//
// Earlier iteration was more aggressive (1 octet / 1 hextet) but most
// users couldn't recognize their own IP in their own session list,
// which defeats the point of showing it.
function maskIp(ip: string | null | undefined): string {
  if (!ip) return '-';
  if (ip.includes(':')) {
    const parts = ip.split(':');
    const head = parts.slice(0, 3).filter((p) => p !== '').join(':');
    return `${head}:****`;
  }
  const [a, b] = ip.split('.');
  return `${a}.${b}.*.*`;
}
